import React, { useState, useEffect, useRef } from 'react';
import { useConfig } from '../config/ConfigContext';
import { useLiveStatus } from '../contexts/LiveStatusContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { videosService } from '../services/videosService';
import { fetchLectureData } from '../services/googleSheets';
import '../styles/videosPage.css';

/**
 * Videos page component - YouTube-style video viewing experience
 */
const VideosPage = () => {
  const { config, theme } = useConfig();
  const { hasLiveVideos } = useLiveStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('videos'); // 'videos' or 'live'
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [videos, setVideos] = useState([]);
  const [allVideos, setAllVideos] = useState([]); // Store all videos for filtering
  const [categories, setCategories] = useState([]);
  const [videoMetadata, setVideoMetadata] = useState([]); // Store metadata from Google Sheets
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [centerData, setCenterData] = useState(null);
  const [lectureData, setLectureData] = useState([]); // Store lecture data from Google Sheets
  const scrollRef = useRef(null);

  // Redirect if video options are not enabled
  useEffect(() => {
    if (config && !config.video_options_enabled) {
      navigate('/');
    }
  }, [config, navigate]);

  // Handle navigation state from video watch page
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      // Clear the state to prevent it from affecting future navigations
      navigate('/videos', { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  // Initialize center data and categories
  useEffect(() => {
    const initializeData = async () => {
      if (!config) return;
      
      try {
        let centerData = null;
        
        // First try to use center_id from config (preferred method)
        if (config.center_id) {
          console.log('[VideosPage] Using center_id from config:', config.center_id);
          centerData = {
            id: config.center_id,
            name: config.name || 'Center'
          };
        } else if (config.domain) {
          // Fallback to domain lookup if no center_id in config
          const domain = config.domain;
          console.log('[VideosPage] No center_id in config, falling back to domain lookup:', domain);
          const center = await videosService.getCenterIdByDomain(domain);
          
          if (center) {
            console.log('[VideosPage] Found center by domain:', center);
            centerData = center;
          }
        }
        
        if (centerData) {
          console.log('[VideosPage] Center data set:', centerData);
          setCenterData(centerData);
          
          // Get series categories from Google Sheets
          const seriesCategories = await videosService.getSeriesCategories(config.google_sheet_id);
          setCategories(seriesCategories);
          
          // Also fetch and store video metadata for later filtering
          const metadata = await videosService.fetchVideoMetadataFromSheets(config.google_sheet_id);
          setVideoMetadata(metadata);
          
          // Fetch lecture data from Google Sheets
          if (config.google_sheet_id) {
            try {
              const lectures = await fetchLectureData(config.google_sheet_id);
              setLectureData(lectures);
              console.log('[VideosPage] Loaded lecture data:', lectures.length);
            } catch (error) {
              console.error('[VideosPage] Error fetching lecture data:', error);
              // Don't fail the whole page if lecture data fails
            }
          }
          
          // Load all videos from Supabase once for enhancement purposes
          const result = await videosService.fetchVideos(centerData.id, 'video', 0, 100);
          setAllVideos(result.videos);
          console.log('[VideosPage] Loaded Supabase videos for enhancement:', result.videos.length);
        } else {
          console.error('[VideosPage] No center found');
          setError('Center not found');
        }
      } catch (error) {
        console.error('Error initializing data:', error);
        setError('Failed to initialize video data');
      }
    };
    
    initializeData();
  }, [config]);

  // Load videos based on active tab and category
  useEffect(() => {
    const loadVideos = async () => {
      if (!centerData || !centerData.id) {
        console.log('[VideosPage] Skipping video load - no center data', centerData);
        return;
      }
      
      console.log('[VideosPage] Loading videos for center:', centerData);
      setLoading(true);
      setError(null);
      
      try {
        // For 'all' category, use Supabase as the source
        if (selectedCategory === 'all') {
          const videoType = activeTab === 'live' ? 'live' : 'video';
          const result = await videosService.fetchVideos(centerData.id, videoType, 0, 100);
          
          console.log('[VideosPage] Videos loaded from Supabase:', result);
          setAllVideos(result.videos);
          setVideos(result.videos);
        } else {
          // For event series categories, use Google Sheets as the primary source
          console.log(`[VideosPage] Using Google Sheets for series "${selectedCategory}"`);
          
          if (videoMetadata.length === 0) {
            console.warn('[VideosPage] No video metadata loaded from Google Sheets');
            setVideos([]);
            return;
          }
          
          // Get all videos for this series from Google Sheets
          const seriesVideos = videoMetadata
            .filter(meta => meta.series === selectedCategory)
            .map(meta => ({
              // Transform Google Sheets metadata to match video format
              id: meta.youtube_video_id,
              youtube_video_id: meta.youtube_video_id,
              title: meta.title,
              thumbnail: meta.youtube_video_id ? 
                `https://img.youtube.com/vi/${meta.youtube_video_id}/hqdefault.jpg` : 
                null,
              duration: '0:00', // Duration not available in sheets
              views: videosService.formatViews(0), // Will be enhanced later
              published: meta.publishedAt ? 
                videosService.formatPublishedDate(meta.publishedAt) : 
                'Date unknown',
              video_type: 'video',
              video_url: meta.videoUrl,
              publishedAt: meta.publishedAt,
              isLiveNow: false,
              view_count: 0,
              speakerName: meta.speakerName,
              eventName: meta.eventName
            }))
            .filter(video => video.youtube_video_id); // Only include videos with valid IDs
          
          console.log(`[VideosPage] Found ${seriesVideos.length} videos for series "${selectedCategory}" in Google Sheets`);
          
          // Optionally enhance with data from Supabase (views, likes)
          if (allVideos.length > 0) {
            seriesVideos.forEach(sheetVideo => {
              const supabaseVideo = allVideos.find(v => v.youtube_video_id === sheetVideo.youtube_video_id);
              if (supabaseVideo) {
                // Enhance with Supabase data
                sheetVideo.views = supabaseVideo.views;
                sheetVideo.view_count = supabaseVideo.view_count;
                sheetVideo.duration = supabaseVideo.duration;
                // Live status from Supabase — without this, the per-card "LIVE"
                // badge never appears even when the row has is_live_now=true.
                sheetVideo.isLiveNow = supabaseVideo.is_live_now === true;
                console.log(`[VideosPage] Enhanced video ${sheetVideo.youtube_video_id} with Supabase data (isLive=${sheetVideo.isLiveNow})`);
              }
            });
          }
          
          setVideos(seriesVideos);
        }
        setHasMore(false); // Disable pagination for now since we're loading all
        setPage(0);
      } catch (error) {
        console.error('Error loading videos:', error);
        setError('Failed to load videos');
      } finally {
        setLoading(false);
      }
    };
    
    loadVideos();
  }, [centerData, activeTab, selectedCategory, videoMetadata]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(0);
    
    // Reset category to 'all' when switching to live tab
    // since categories are only relevant for videos
    if (tab === 'live') {
      setSelectedCategory('all');
    }
  };

  // Handle category change
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setPage(0);
  };

  // Scroll selected series into view
  useEffect(() => {
    if (scrollRef.current && selectedCategory !== 'all') {
      const selectedElement = scrollRef.current.querySelector(`[data-series="${selectedCategory}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest', 
          inline: 'center' 
        });
      }
    }
  }, [selectedCategory]);

  // Load more videos (disabled for now since we load all videos for filtering)
  const loadMoreVideos = async () => {
    // Pagination disabled when using Google Sheets filtering
    return;
  };

  const themeColor = config?.primaryColor || theme?.colors?.primary || '#1e4d8b';

  // Helper function to check if a video has associated lecture notes
  const hasLectureNotes = (video) => {
    if (!lectureData || lectureData.length === 0) return false;
    
    // Check if the video URL matches any lecture's fullVideoUrl
    const videoUrl = video.video_url || `https://youtube.com/watch?v=${video.youtube_video_id}`;
    
    return lectureData.some(lecture => {
      // Check both full video URL and summary video URL
      const hasMatch = (lecture.fullVideoUrl && lecture.fullVideoUrl.includes(video.youtube_video_id)) ||
                      (lecture.summaryVideoUrl && lecture.summaryVideoUrl.includes(video.youtube_video_id));
      
      // Also check if lecture has notes available
      return hasMatch && (lecture.keyTakeawaysUrl || lecture.detailedNotesUrl);
    });
  };

  if (loading) {
    return (
      <div className="videos-page-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="videos-page-error">
        <p>Error loading videos: {error}</p>
      </div>
    );
  }

  return (
    <div className="videos-page">
      {/* Top Toggle: Videos | Live */}
      <div className="videos-top-toggle">
        <button
          className={`toggle-button ${activeTab === 'videos' ? 'active' : ''}`}
          onClick={() => handleTabChange('videos')}
          style={{
            backgroundColor: activeTab === 'videos' ? themeColor : '#f8f9fa',
            color: activeTab === 'videos' ? '#fff' : '#666',
            border: activeTab === 'videos' ? 'none' : '1px solid #e0e0e0'
          }}
        >
          Videos
        </button>
        <button
          className={`toggle-button ${activeTab === 'live' ? 'active' : ''} ${hasLiveVideos ? 'has-live' : ''}`}
          onClick={() => handleTabChange('live')}
          style={{
            backgroundColor: activeTab === 'live' ? themeColor : '#f8f9fa',
            color: activeTab === 'live' ? '#fff' : '#666',
            border: activeTab === 'live' ? 'none' : '1px solid #e0e0e0'
          }}
        >
          Live
          {hasLiveVideos && <span className="live-dot-indicator" />}
        </button>
      </div>

      {/* Two-Row Category Navigation - Only show for Videos tab */}
      {activeTab === 'videos' && (
        <div className="videos-series-navigation">
          {/* Row 1: Selected Series */}
          <div className="videos-nav-selected">
            <div className="selected-series" style={{ backgroundColor: themeColor }}>
              <span className="series-name">
                {selectedCategory === 'all' 
                  ? 'All Videos' 
                  : categories.find(c => c.id === selectedCategory)?.eventName || selectedCategory}
              </span>
              <span className="checkmark">✓</span>
            </div>
          </div>

          {/* Row 2: Other Series (scrollable) */}
          <div className="videos-nav-other" ref={scrollRef}>
            {categories
              .filter(category => category.id !== selectedCategory)
              .map((category) => (
                <button
                  key={category.id}
                  className="series-option"
                  onClick={() => handleCategoryChange(category.id)}
                  data-series={category.id}
                >
                  {category.eventName || category.name}
                </button>
              ))}
            
            {/* Scroll indicator if there are more series */}
            {categories.filter(c => c.id !== selectedCategory).length > 3 && (
              <div className="scroll-indicator">
                <span>›</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Videos List */}
      <div className="videos-list">
        {videos.length > 0 ? (
          videos.map((video) => (
            <div 
              key={video.id} 
              className={`video-card ${video.isLiveNow ? 'is-live' : ''}`}
              onClick={() => navigate(`/videos/${video.id}`, { state: { autoplay: true } })}
            >
              <div className="video-thumbnail">
                <img src={video.thumbnail} alt={video.title} />
                <span className="video-duration">{video.duration}</span>
                {video.isLiveNow && <span className="live-indicator">LIVE</span>}
                {hasLectureNotes(video) && (
                  <span className="notes-indicator">
                    <span className="notes-icon">📝</span>
                    <span className="notes-text">Notes</span>
                  </span>
                )}
              </div>
              <div className="video-info">
                <h3 className="video-title">{video.title}</h3>
                <div className="video-metadata">
                  <span className="video-center">@{centerData?.name || 'Center'}</span>
                  <span className="video-views">{video.views} views</span>
                  <span className="video-published">• {video.published}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-videos">
            <p>{activeTab === 'live' ? 'No live streams available' : 'No videos available'}</p>
          </div>
        )}
        
        {/* Load More Button */}
        {hasMore && videos.length > 0 && (
          <div className="load-more-container">
            <button
              className="load-more-button"
              onClick={loadMoreVideos}
              disabled={loading}
              style={{
                backgroundColor: themeColor,
                color: '#fff'
              }}
            >
              {loading ? 'Loading...' : 'Load More Videos'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideosPage;